"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Pencil, X } from "lucide-react";
import PostEditor from "@/app/community/restoration/PostEditor";
import PostCard from "@/app/community/restoration/PostCard";
import ProblemAccordion from "@/app/community/restoration/ProblemAccordion";
import SessionAdminBar from "@/app/community/restoration/SessionAdminBar";
import { Button } from "@/components/ui/button";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import { useScrollSpy } from "@/hooks/useScrollSpy";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { cn, scrollToAnchor } from "@/lib/utils";
import { getMyVotesForSession } from "@/lib/server-actions/restoration/votes";
import type { RestorationExamSession, RestorationPost, RestorationProblemMeta } from "@/lib/server-actions/restoration/_shared";

interface RestorationPageClientProps {
  sessions: RestorationExamSession[];
  currentSessionId: number | null;
  problemMeta: RestorationProblemMeta[];
  posts: RestorationPost[];
}

const problemAnchorId = (n: number) => `restoration-problem-${n}`;
const UNASSIGNED_ANCHOR = "restoration-unassigned";
/** 로그인 후 복원 게시판으로 복귀시키는 sign-in 링크 (classic·shell 공용). */
const SIGN_IN_HREF = "/sign-in?redirect_url=/community/restoration";
const EMPTY_MY_VOTES: Record<number, "up" | "down"> = {};

/**
 * JCG-1569: 복원 게시판 메인 페이지 클라이언트 조합.
 * JCG-2102: UI 2.0 3컬럼 셸 적용 — 데스크탑 셸 안에서는 회차 셀렉터(상단 바)·문제 네비(좌)·
 * 도구+작성 모드(우)를 셸 슬롯에 portal 주입하고, 본문엔 복원 글 섹션만 둔다. 모바일/비셸에서는
 * 기존 단일 컬럼 레이아웃(회차 탭·인라인 에디터·3섹션)을 그대로 유지한다(`inShell` 분기).
 *
 * 로컬 상태로 posts를 들고 있어 투표 결과를 즉시 반영하고, 수정·삭제 등 구조적 변경은
 * `router.refresh()`로 서버에서 다시 받아 일관성을 유지한다.
 */
export default function RestorationPageClient({
  sessions,
  currentSessionId,
  problemMeta,
  posts: initialPosts,
}: RestorationPageClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    isLoaded: isAuthLoaded,
    isSignedIn,
    userId: currentUserId,
    isAdmin,
  } = useAdminStatus();
  const {
    inShell,
    shellHeaderSlot,
    restorationLeftSlot,
    restorationRightSlot,
    composeSlot,
    rightMode,
    enterCompose,
    exitCompose,
  } = useArticleShellSlots();

  const [posts, setPosts] = useState(initialPosts);
  // 댓글 딥링크 `?post={id}#comment-{uuid}` (JCG-3603): 대상 게시물의 문제 아코디언과 카드를 펼친 채
  // 시작해야 RestorationCommentSection 이 마운트되어 해시를 이어받는다. 게시물이 없으면(삭제·다른 회차) 무시.
  const targetPostId = useMemo(() => {
    const raw = searchParams?.get("post");
    const parsed = raw ? Number(raw) : Number.NaN;
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);
  const targetProblemNumber = useMemo(() => {
    if (targetPostId === null) return null;
    return initialPosts.find((p) => p.id === targetPostId)?.problem_number ?? null;
  }, [initialPosts, targetPostId]);
  const activeVotesSessionId =
    isAuthLoaded && isSignedIn && currentSessionId ? currentSessionId : null;
  const [myVotesState, setMyVotesState] = useState<{
    sessionId: number;
    userId: string | null;
    votes: Record<number, "up" | "down">;
  } | null>(null);
  const myVotes =
    activeVotesSessionId !== null &&
    myVotesState?.sessionId === activeVotesSessionId &&
    myVotesState.userId === currentUserId
      ? myVotesState.votes
      : EMPTY_MY_VOTES;
  // 서버 재조회(router.refresh, 회차 전환 등)로 initialPosts prop이 갱신되면 로컬 posts도 동기화.
  // useEffect 대신 렌더 시점 prev-prop 비교 패턴 사용 (React 권장: storing-information-from-previous-renders).
  const [prevInitialPosts, setPrevInitialPosts] = useState(initialPosts);
  // 비셸(클래식) 레이아웃의 인라인 에디터 토글. 셸에서는 rightMode === "compose" 가 대신한다.
  const [editorOpen, setEditorOpen] = useState(false);
  // 셸 좌측 네비에서 펼친 문제 번호 집합(controlled) — 네비 클릭 시 해당 아코디언을 펼친다.
  const [openProblems, setOpenProblems] = useState<Set<number>>(() =>
    targetProblemNumber !== null ? new Set([targetProblemNumber]) : new Set()
  );
  if (prevInitialPosts !== initialPosts) {
    setPrevInitialPosts(initialPosts);
    setPosts(initialPosts);
    // 회차 전환 등으로 게시물 집합이 바뀌면 아코디언 펼침 상태도 리셋 — 직전 회차에서 펼친
    // 문제 번호가 새 회차로 누수돼 엉뚱한 아코디언이 펼쳐진 채 렌더되는 것을 방지(JCG-2123 #4).
    setOpenProblems(new Set());
  }

  useEffect(() => {
    if (activeVotesSessionId === null) return;

    const sessionId = activeVotesSessionId;
    let cancelled = false;

    async function loadMyVotes() {
      try {
        const nextVotes = await getMyVotesForSession(sessionId);
        if (!cancelled) {
          setMyVotesState({
            sessionId,
            userId: currentUserId,
            votes: nextVotes,
          });
        }
      } catch (error) {
        console.error("Failed to load restoration votes", error);
        if (!cancelled) {
          setMyVotesState({
            sessionId,
            userId: currentUserId,
            votes: EMPTY_MY_VOTES,
          });
        }
      }
    }

    loadMyVotes();
    return () => {
      cancelled = true;
    };
  }, [activeVotesSessionId, currentUserId]);

  // --- posts 로컬 업데이트 ---

  const handlePostChanged = useCallback(
    (postId: number, next: Partial<RestorationPost> | null) => {
      if (next === null) {
        setPosts((prev) => prev.filter((p) => p.id !== postId));
        return;
      }
      if (Object.keys(next).length === 0) return;
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, ...next } : p))
      );
    },
    []
  );

  const handleRefreshNeeded = useCallback(() => {
    router.refresh();
  }, [router]);

  // --- 분류: 1~20번 + 미분류 ---

  const byProblemNumber = useMemo(() => {
    const map = new Map<number, RestorationPost[]>();
    for (const p of posts) {
      if (p.problem_number === null) continue;
      const arr = map.get(p.problem_number) ?? [];
      arr.push(p);
      map.set(p.problem_number, arr);
    }
    return map;
  }, [posts]);

  const unassignedPosts = useMemo(
    () => posts.filter((p) => p.problem_number === null),
    [posts]
  );

  const classifiedMeta = useMemo(
    () =>
      problemMeta.filter(
        (m) => (byProblemNumber.get(m.problem_number)?.length ?? 0) > 0
      ),
    [problemMeta, byProblemNumber]
  );

  const emptyMeta = useMemo(
    () =>
      problemMeta.filter(
        (m) => (byProblemNumber.get(m.problem_number)?.length ?? 0) === 0
      ),
    [problemMeta, byProblemNumber]
  );

  const currentSession = useMemo(
    () => sessions.find((s) => s.id === currentSessionId) ?? null,
    [sessions, currentSessionId]
  );

  const sessionLocked =
    currentSession?.is_write_locked === true && !isAdmin;

  // --- 회차 탭 링크 helper ---

  const buildSessionHref = useCallback(
    (sessionId: number) => {
      const params = new URLSearchParams(searchParams?.toString() ?? "");
      params.set("session", String(sessionId));
      return `/community/restoration?${params.toString()}`;
    },
    [searchParams]
  );

  // --- 셸 네비 상호작용 ---

  const toggleProblem = useCallback((n: number) => {
    setOpenProblems((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  }, []);

  // 네비 칩 클릭: 해당 아코디언을 펼친 뒤 스크롤 이동(미복원 문제도 앵커가 있어 이동 가능).
  // setOpenProblems 는 비동기 펼침이라 즉시 scrollIntoView 하면 펼침 전(접힌) 레이아웃 기준으로
  // 오프셋이 어긋난다 → double rAF 로 펼침이 DOM·레이아웃에 반영된 뒤 스크롤한다(JCG-2123 #5).
  const navigateToProblem = useCallback((n: number) => {
    setOpenProblems((prev) => {
      if (prev.has(n)) return prev;
      const next = new Set(prev);
      next.add(n);
      return next;
    });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollToAnchor(problemAnchorId(n)));
    });
  }, []);

  const navigateToUnassigned = useCallback(() => {
    scrollToAnchor(UNASSIGNED_ANCHOR);
  }, []);

  // 작성 모드 등록 성공 시 — 셸/비셸 공통 후처리(낙관적 추가 + 서버 재조회).
  const handleEditorSuccess = useCallback(
    (newPost: RestorationPost | null) => {
      if (newPost && newPost.session_id === currentSessionId) {
        setPosts((prev) =>
          prev.some((p) => p.id === newPost.id) ? prev : [newPost, ...prev]
        );
      }
      router.refresh();
    },
    [currentSessionId, router]
  );

  // === 회차 없음 ===
  // JCG-2123 #1: 회차 0개 early return 이 inShell portal 분기보다 먼저 실행되면 데스크탑 셸에서
  // 좌/우/헤더 슬롯이 비고 SessionAdminBar 가 중앙에 인라인으로 떨어진다. → early return 대신
  // 플래그로 두고, 셸이면 portal 분기 안에서(중앙=안내, 우측 슬롯=admin 바), 비셸이면 기존 마크업
  // 그대로 렌더한다.
  const noSessions = sessions.length === 0;

  if (noSessions) {
    // emptyNotice 는 회차 0개일 때만 쓰이므로 이 블록 안에서만 생성(공통 경로 불필요 할당 방지).
    const emptyNotice = (
      <div
        className={cn(
          "rounded-md border border-dashed border-gray-300 dark:border-neutral-600 p-8 text-center text-sm text-gray-500 dark:text-neutral-400",
          !inShell && "mx-auto w-full max-w-[800px]"
        )}
      >
        아직 등록된 시험 회차가 없습니다. 관리자가 회차를 먼저 생성해주세요.
      </div>
    );
    // 셸: 중앙엔 안내, 우측 도구 슬롯엔 admin 회차 생성 바(좌/헤더 슬롯은 회차가 없으니 비움).
    if (inShell) {
      return (
        <>
          <div className="flex flex-col gap-4">{emptyNotice}</div>

          {isAdmin &&
            restorationRightSlot &&
            createPortal(
              <SessionAdminBar
                currentSession={null}
                onChanged={() => router.refresh()}
              />,
              restorationRightSlot
            )}
        </>
      );
    }

    // 비셸(클래식): 기존 마크업 보존 — admin 바 + 안내를 표준 너비로 세로 스택.
    return (
      <div className="flex flex-col gap-4">
        {isAdmin && (
          <div className="mx-auto w-full max-w-[800px]">
            <SessionAdminBar currentSession={null} onChanged={() => router.refresh()} />
          </div>
        )}
        {emptyNotice}
      </div>
    );
  }

  // === 본문 섹션 (셸/비셸 공통) ===
  const sectionWidth = inShell ? "w-full" : "mx-auto w-full max-w-[800px]";

  // 셸일 때만 controlled open/anchor 를 주입(네비 연동). 비셸은 uncontrolled 기본 동작.
  // 딥링크 대상 게시물(`?post=`)의 문제는 양쪽 모두 펼친 채 시작한다(셸은 openProblems 초기값으로).
  const accordionControl = (n: number) =>
    inShell
      ? {
          anchorId: problemAnchorId(n),
          open: openProblems.has(n),
          onToggle: () => toggleProblem(n),
          expandedPostId: targetPostId,
        }
      : { defaultOpen: n === targetProblemNumber, expandedPostId: targetPostId };

  const bodySections = (
    <div className="flex flex-col gap-6">
      {/* === 상단: 분류된 아코디언 === */}
      {classifiedMeta.length > 0 && (
        <section className={cn(sectionWidth, "flex flex-col gap-2")}>
          <h2 className="text-lg font-semibold">
            {currentSession?.label ?? ""} · 복원 진행 중
          </h2>
          <div className="flex flex-col gap-2">
            {classifiedMeta.map((meta) => (
              <ProblemAccordion
                key={meta.id}
                meta={meta}
                posts={byProblemNumber.get(meta.problem_number) ?? []}
                myVotes={myVotes}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                sessions={sessions}
                onPostChanged={handlePostChanged}
                onRefreshNeeded={handleRefreshNeeded}
                {...accordionControl(meta.problem_number)}
              />
            ))}
          </div>
        </section>
      )}

      {/* === 중간: 미분류 === */}
      <section id={inShell ? UNASSIGNED_ANCHOR : undefined} className={cn(sectionWidth, "flex flex-col gap-3 navbar-scroll-mt-20")}>
        <h2 className="text-lg font-semibold">
          미분류 ({unassignedPosts.length})
        </h2>
        <p className="text-xs text-gray-500 dark:text-neutral-400">
          아직 관리자가 1~20번 중 어느 문제인지 맵핑하지 않은 게시물입니다.
        </p>
        {unassignedPosts.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-300 dark:border-neutral-600 p-6 text-center text-sm text-gray-500 dark:text-neutral-400">
            미분류 게시물이 없습니다.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {unassignedPosts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                myVote={myVotes[post.id] ?? null}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                sessions={sessions}
                initialExpanded={post.id === targetPostId}
                onPostChanged={handlePostChanged}
                onRefreshNeeded={handleRefreshNeeded}
              />
            ))}
          </div>
        )}
      </section>

      {/* === 하단: 아직 복원되지 않은 문제 === */}
      {emptyMeta.length > 0 && (
        <section className={cn(sectionWidth, "flex flex-col gap-2")}>
          <h2 className="text-lg font-semibold">아직 복원되지 않은 문제</h2>
          <div className="flex flex-col gap-2">
            {emptyMeta.map((meta) => (
              <ProblemAccordion
                key={meta.id}
                meta={meta}
                posts={byProblemNumber.get(meta.problem_number) ?? []}
                myVotes={myVotes}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                sessions={sessions}
                onPostChanged={handlePostChanged}
                onRefreshNeeded={handleRefreshNeeded}
                {...accordionControl(meta.problem_number)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );

  // === 셸 레이아웃 (데스크탑) ===
  if (inShell) {
    const editor = (
      <PostEditor
        sessions={sessions}
        isAdmin={isAdmin}
        initialSessionId={currentSessionId ?? undefined}
        onSuccess={(newPost) => {
          exitCompose();
          handleEditorSuccess(newPost);
        }}
        onCancel={exitCompose}
      />
    );

    return (
      <>
        {bodySections}

        {/* 상단 바: 회차 셀렉터 */}
        {shellHeaderSlot &&
          createPortal(
            <SessionTabs
              variant="shell"
              sessions={sessions}
              currentSessionId={currentSessionId}
              buildSessionHref={buildSessionHref}
            />,
            shellHeaderSlot
          )}

        {/* 좌측: 진행바 + 문제 네비 */}
        {restorationLeftSlot &&
          createPortal(
            <RestorationNav
              problemMeta={problemMeta}
              byProblemNumber={byProblemNumber}
              classifiedCount={classifiedMeta.length}
              unassignedCount={unassignedPosts.length}
              onNavigateProblem={navigateToProblem}
              onNavigateUnassigned={navigateToUnassigned}
            />,
            restorationLeftSlot
          )}

        {/* 우측 도구(작성 진입·로그인·통계·admin) */}
        {restorationRightSlot &&
          createPortal(
            <RestorationTools
              isSignedIn={isSignedIn}
              isAdmin={isAdmin}
              sessionLocked={sessionLocked}
              currentSession={currentSession}
              onCompose={enterCompose}
              onSessionChanged={() => router.refresh()}
            />,
            restorationRightSlot
          )}

        {/* 우측 작성 모드: PostEditor */}
        {rightMode === "compose" && composeSlot && createPortal(editor, composeSlot)}
      </>
    );
  }

  // === 클래식 레이아웃 (모바일/비셸) — 기존 마크업 보존 ===
  return (
    <div className="flex flex-col gap-6">
      {/* === 관리자 세션 관리 (JCG-1572) === */}
      {isAdmin && (
        <div className="mx-auto w-full max-w-[800px]">
          <SessionAdminBar
            currentSession={currentSession}
            onChanged={() => router.refresh()}
          />
        </div>
      )}

      {/* === 회차 탭 === */}
      <SessionTabs
        variant="classic"
        sessions={sessions}
        currentSessionId={currentSessionId}
        buildSessionHref={buildSessionHref}
      />

      {/* === 에디터 토글 버튼 / 로그인 안내 (표준 너비) === */}
      <div className="mx-auto w-full max-w-[800px]">
        {!isSignedIn ? (
          <div className="rounded-md border border-gray-200 dark:border-neutral-700 p-4 text-sm text-gray-600 dark:text-neutral-300 flex items-center justify-between gap-3 flex-wrap">
            <span>복원 문제를 작성하려면 로그인이 필요합니다.</span>
            <Link
              href={SIGN_IN_HREF}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
            >
              로그인
            </Link>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setEditorOpen((v) => !v)}
              disabled={sessionLocked && !editorOpen}
              className={
                "inline-flex items-center gap-1 rounded-md px-4 py-2 text-sm font-medium transition-colors " +
                (editorOpen
                  ? "border border-gray-300 dark:border-neutral-600 text-gray-700 dark:text-neutral-200 hover:bg-gray-50 dark:hover:bg-neutral-800"
                  : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed")
              }
            >
              {editorOpen ? (
                <>
                  <X className="w-4 h-4" />
                  에디터 닫기
                </>
              ) : (
                <>
                  <Pencil className="w-4 h-4" />
                  복원 문제 작성
                </>
              )}
            </button>
            {sessionLocked && !editorOpen && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                이 회차는 글쓰기가 잠겼습니다. 관리자만 작성할 수 있습니다.
              </p>
            )}
          </>
        )}
      </div>

      {/* === 에디터 본체 (전체 너비, 부모 1600px 상속) === */}
      {isSignedIn && editorOpen && (
        <div className="w-full">
          <div className="rounded-md border border-gray-200 dark:border-neutral-700 p-4">
            <PostEditor
              sessions={sessions}
              isAdmin={isAdmin}
              initialSessionId={currentSessionId ?? undefined}
              onSuccess={(newPost) => {
                setEditorOpen(false);
                handleEditorSuccess(newPost);
              }}
              onCancel={() => setEditorOpen(false)}
            />
          </div>
        </div>
      )}

      {bodySections}
    </div>
  );
}

/* =========================================================
   셸 슬롯 내부 컴포넌트 (JCG-2102)
   ========================================================= */

interface SessionTabsProps {
  sessions: RestorationExamSession[];
  currentSessionId: number | null;
  buildSessionHref: (id: number) => string;
  /** classic: 본문 상단 밑줄 탭 / shell: 상단 바 슬림 pill 셀렉터 */
  variant: "classic" | "shell";
}

/**
 * 회차 탭 — classic(본문 밑줄 탭)·shell(상단 바 pill) 공용. 회차 목록 iteration + 활성/잠김
 * 표시 로직을 한 곳에 모으고 스타일만 variant 로 분기한다(JCG-2102 정리).
 */
function SessionTabs({ sessions, currentSessionId, buildSessionHref, variant }: SessionTabsProps) {
  const classic = variant === "classic";
  return (
    <nav
      aria-label="시험 회차"
      className={cn(
        "flex items-center overflow-x-auto",
        classic
          ? "mx-auto w-full max-w-[800px] gap-2 border-b border-gray-200 dark:border-neutral-700 pb-0"
          : "min-w-0 gap-1"
      )}
    >
      {sessions.map((s) => {
        const active = s.id === currentSessionId;
        return (
          <Link
            key={s.id}
            href={buildSessionHref(s.id)}
            prefetch={false}
            className={cn(
              "shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
              classic
                ? cn(
                    "-mb-px border-b-2 px-4 py-2 text-sm",
                    active
                      ? "border-blue-600 font-semibold text-blue-700 dark:text-blue-400"
                      : "border-transparent text-gray-600 hover:text-gray-900 dark:text-neutral-400 dark:hover:text-neutral-100"
                  )
                : cn(
                    "rounded-lg px-3 py-1.5 text-[13px] font-bold",
                    active
                      ? "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )
            )}
          >
            {s.label}
            {s.is_write_locked && (
              <span
                className={cn(
                  "ml-1 text-amber-600 dark:text-amber-400",
                  classic ? "text-xs" : "text-[11px]"
                )}
              >
                (잠김)
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

interface RestorationNavProps {
  problemMeta: RestorationProblemMeta[];
  byProblemNumber: Map<number, RestorationPost[]>;
  classifiedCount: number;
  unassignedCount: number;
  onNavigateProblem: (n: number) => void;
  onNavigateUnassigned: () => void;
}

/** 좌측 사이드바 — 진행 헤더 + 1~20 문제 네비(scroll-spy) + 미분류 칩. */
function RestorationNav({
  problemMeta,
  byProblemNumber,
  classifiedCount,
  unassignedCount,
  onNavigateProblem,
  onNavigateUnassigned,
}: RestorationNavProps) {
  const total = problemMeta.length || 20;

  // scroll-spy mount-order race 회피(JCG-2123 #3): 관찰 대상 <section id> 는 RestorationNav 와
  // 다른 subtree(bodySections)에서 렌더되므로, 포털된 이 네비의 useScrollSpy effect 가 먼저
  // 돌면 useScrollSpy 내부 getElementById 가 빈 결과를 받아 IntersectionObserver 가 미부착된다.
  // → 마운트 후 rAF 로 readyTick 을 한 번 올려 anchorKeys 참조를 갱신하면, 그 시점엔 형제
  // 본문 섹션이 DOM 에 커밋돼 있어 useScrollSpy effect 가 재실행되며 IO 가 정상 부착된다.
  // problemMeta 변경(회차 전환 등 섹션 재구성) 시에도 다시 재부착을 시도한다.
  const [readyTick, setReadyTick] = useState(0);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setReadyTick((t) => t + 1));
    return () => cancelAnimationFrame(raf);
  }, [problemMeta]);

  // scroll-spy: 현재 화면 상단에 가장 가까운 문제 섹션을 active 로 추적(공용 훅, ProblemListNav 공유).
  // readyTick 을 deps 에 포함해 본문 섹션 마운트 후 배열 참조가 바뀌면서 useScrollSpy 가 재부착되게 한다.
  const anchorKeys = useMemo(
    () => [
      ...problemMeta.map((m) => problemAnchorId(m.problem_number)),
      UNASSIGNED_ANCHOR,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [problemMeta, readyTick]
  );
  const activeAnchor = useScrollSpy(anchorKeys, { rootMargin: "-15% 0px -75% 0px" });

  const progressPct = total === 0 ? 0 : Math.round((classifiedCount / total) * 100);

  return (
    <div className="flex flex-col gap-3">
      {/* 진행 헤더 */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between">
          <span className="text-[13px] font-extrabold text-foreground">복원 진행</span>
          <span className="text-[12px] font-bold tabular-nums text-amber-600 dark:text-amber-400">
            {classifiedCount}/{total} 복원됨
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-amber-500 transition-all dark:bg-amber-400"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* 문제 칩 — SessionSelector(SessionTabs)의 <nav aria-label> 패턴과 일관되게 list 시맨틱 부여(JCG-2123 #7). */}
      <nav aria-label="문제 네비게이션">
       <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {problemMeta.map((m) => {
          const n = m.problem_number;
          const count = byProblemNumber.get(n)?.length ?? 0;
          const restored = count > 0;
          const anchor = problemAnchorId(n);
          const isActive = activeAnchor === anchor;
          return (
            <li key={m.id}>
            <button
              type="button"
              onClick={() => onNavigateProblem(n)}
              aria-current={isActive ? "true" : undefined}
              className={cn(
                "flex w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
                isActive
                  ? "border-amber-400 bg-amber-50 dark:border-amber-500/60 dark:bg-amber-950/40"
                  : "border-border bg-card hover:border-amber-300 dark:hover:border-amber-700"
              )}
            >
              <span
                className={cn(
                  "flex h-6 min-w-[1.5rem] shrink-0 items-center justify-center rounded-md px-1 text-[11px] font-bold tabular-nums",
                  restored
                    ? "bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {n}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12.5px] text-foreground/80">
                {m.summary || `${n}번 문제`}
              </span>
              <span
                className={cn(
                  "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold",
                  restored
                    ? "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {restored ? `복원중 ${count}` : "미복원"}
              </span>
            </button>
            </li>
          );
        })}

        {/* 미분류 칩 */}
        {unassignedCount > 0 && (
          <li className="mt-1">
          <button
            type="button"
            onClick={onNavigateUnassigned}
            aria-current={activeAnchor === UNASSIGNED_ANCHOR ? "true" : undefined}
            className={cn(
              "flex w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
              activeAnchor === UNASSIGNED_ANCHOR
                ? "border-amber-400 bg-amber-50 dark:border-amber-500/60 dark:bg-amber-950/40"
                : "border-dashed border-border bg-card hover:border-amber-300 dark:hover:border-amber-700"
            )}
          >
            <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-foreground/80">
              미분류
            </span>
            <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">
              {unassignedCount}개
            </span>
          </button>
          </li>
        )}
       </ul>
      </nav>
    </div>
  );
}

interface RestorationToolsProps {
  isSignedIn: boolean;
  isAdmin: boolean;
  sessionLocked: boolean;
  currentSession: RestorationExamSession | null;
  onCompose: () => void;
  onSessionChanged: () => void;
}

/** 우측 사이드바 도구 — 상단 고정 (admin)세션 관리 + 중앙 작성 진입 버튼만. */
function RestorationTools({
  isSignedIn,
  isAdmin,
  sessionLocked,
  currentSession,
  onCompose,
  onSessionChanged,
}: RestorationToolsProps) {
  return (
    <div className="flex h-full flex-col gap-3">
      {/* 관리자 세션 관리 — 상단 고정(관리자 전용, 비관리자는 미렌더) */}
      {isAdmin && (
        <div className="shrink-0">
          <SessionAdminBar currentSession={currentSession} onChanged={onSessionChanged} />
        </div>
      )}

      {/* 작성 진입 — 남은 공간 중앙에 카드형 CTA */}
      <div className="flex flex-1 flex-col items-center justify-center px-1">
        <div className="w-full rounded-2xl border border-border bg-card p-5 text-center shadow-sm">
          <div className="mx-auto mb-3 flex size-11 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400">
            <Pencil className="size-5" aria-hidden="true" />
          </div>
          <p className="mb-1 text-[15px] font-bold text-foreground">복원 문제 작성</p>
          {!isSignedIn ? (
            <>
              <p className="mb-4 text-[13px] leading-relaxed text-muted-foreground">
                작성하려면 로그인이 필요합니다.
              </p>
              <Button asChild className="w-full">
                <Link href={SIGN_IN_HREF}>로그인</Link>
              </Button>
            </>
          ) : (
            <>
              <p className="mb-4 text-[13px] leading-relaxed text-muted-foreground">
                기억나는 문제를 함께 복원해 주세요.
              </p>
              <Button
                type="button"
                size="lg"
                onClick={onCompose}
                disabled={sessionLocked}
                className="w-full"
              >
                <Pencil aria-hidden="true" />
                복원 문제 작성
              </Button>
              {sessionLocked && (
                <p className="mt-3 text-[12px] leading-relaxed text-amber-600 dark:text-amber-400">
                  이 회차는 글쓰기가 잠겼습니다.
                  <br />
                  관리자만 작성할 수 있습니다.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
